import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserSession, clearCurrentUserSession } from "../../store/slices/userSessionSlice";
import logo from "../../assets/images/logo-samplee.png";

export default function UserSessionPrint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentUserSession, isLoading, error } = useSelector((state) => state.userSessions || {});

    useEffect(() => {
        if (id) {
            dispatch(fetchUserSession(id));
        }
        return () => {
            dispatch(clearCurrentUserSession());
        };
    }, [dispatch, id]);

    useEffect(() => {
        if (currentUserSession && currentUserSession.id === parseInt(id)) {
            const timer = setTimeout(() => window.print(), 800);
            window.onafterprint = () => navigate(-1);
            return () => {
                clearTimeout(timer);
                window.onafterprint = null;
            };
        }
    }, [currentUserSession, id, navigate]);

    if (isLoading) return <div className="p-8 text-center">Loading User Session Details...</div>;
    if (error) return <div className="p-8 text-danger text-center">{error}</div>;
    if (!currentUserSession) return <div className="p-8 text-center">No session record found.</div>;

    const s = currentUserSession;

    const fmtDate = (dateStr) =>
        dateStr ? new Date(dateStr).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        }) : "";

    const formatTime = (timeStr) => {
        if (!timeStr) return "";
        return timeStr.substring(0, 5);
    };

    return (
        <>
            <style jsx global>{`
                * { margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; font-family: Arial, sans-serif !important; }
                body { background: #fff !important; color: #000 !important; font-size: 13px !important; }
                .print-container { padding: 40px !important; position: relative !important; }
                .logo { position: absolute !important; top: 20px !important; left: 20px !important; }
                .logo img { height: 70px !important; width: auto !important; }
                
                .header { text-align: center !important; margin-bottom: 30px !important; border-bottom: 2px solid #000 !important; padding-bottom: 10px !important; }
                .header h3 { font-size: 1.7rem !important; font-weight: bold !important; }
                .header h6 { font-size: 1.1rem !important; text-transform: uppercase !important; margin-top: 5px !important; }

                .section { margin-bottom: 25px !important; border: 1px solid #000 !important; }
                .section-header { background: #f2f2f2 !important; padding: 8px 12px !important; font-weight: bold !important; border-bottom: 1px solid #000 !important; text-transform: uppercase !important; }
                .section-body { padding: 15px !important; }

                .info-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 15px !important; margin-bottom: 15px !important; }
                .underline { border-bottom: 1px solid #000 !important; font-weight: bold !important; padding: 0 5px !important; display: inline-block !important; min-width: 150px !important; }

                .text-area { border: 1px solid #ccc !important; padding: 10px !important; min-height: 180px !important; margin-top: 5px !important; background: #fff !important; line-height: 1.5 !important; white-space: pre-wrap !important; }

                .satisfaction-box { display: inline-block !important; padding: 4px 8px !important; border: 1px solid #000 !important; font-weight: bold !important; background: #f2f2f2 !important; }

                .footer { margin-top: 50px !important; border-top: 1.5px solid #000 !important; padding-top: 10px !important; text-align: center !important; font-size: 11px !important; }
                .sig-row { display: flex !important; justify-content: space-between !important; margin-top: 40px !important; }
                .sig-box { border-top: 1px solid #000 !important; width: 220px !important; text-align: center !important; padding-top: 5px !important; font-weight: bold !important; }
            `}</style>

            <div className="print-container">
                <div className="logo"><img src={logo} alt="Logo" /></div>
                <div className="header">
                    <h3>THE RECOVERY HOUSE</h3>
                    <h6>USER SESSION CLINICAL NOTE</h6>
                </div>

                {/* Section 1: Client & Session details */}
                <div className="section">
                    <div className="section-header">1. Client &amp; Session Information</div>
                    <div className="section-body">
                        <div className="info-grid">
                            <div>Client Name: <span className="underline">{s.patient ? `${s.patient.first_name || ""} ${s.patient.last_name || ""}`.trim() : "N/A"}</span></div>
                            <div>MR No.: <span className="underline">{s.patient?.mr_no || "N/A"}</span></div>
                        </div>
                        <div className="info-grid">
                            <div>Session Date: <span className="underline">{fmtDate(s.date)}</span></div>
                            <div>Session Time: <span className="underline">{formatTime(s.time)}</span></div>
                        </div>
                        <div className="info-grid">
                            <div>Session Number: <span className="underline">{s.session_number || "—"}</span></div>
                            <div>Program / Category: <span className="underline">{s.category?.name || "—"}</span></div>
                        </div>
                        <div className="info-grid">
                            <div>Session Type: <span className="underline">{s.session_master?.name || "—"}</span></div>
                            <div>Sub Session Type: <span className="underline">{s.sub_session_master?.name || "—"}</span></div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Session Summary / Progress Note */}
                <div className="section">
                    <div className="section-header">2. Summary of Session / Progress Note</div>
                    <div className="section-body">
                        <div className="text-area">
                            {s.summary_of_session || "No session summary recorded."}
                        </div>
                    </div>
                </div>

                {/* Section 3: Client Satisfaction */}
                <div className="section">
                    <div className="section-header">3. Client Satisfaction &amp; Feedback</div>
                    <div className="section-body">
                        <div className="info-grid" style={{ marginBottom: '10px' }}>
                            <div>Client Satisfaction: <span className="satisfaction-box">{s.satisfaction || "N/A"}</span></div>
                        </div>
                        <div style={{ fontWeight: 'bold', marginTop: '10px' }}>Satisfaction Feedback:</div>
                        <div className="text-area" style={{ minHeight: '80px' }}>
                            {s.satisfaction_feedback || "No feedback recorded."}
                        </div>
                    </div>
                </div>

                {/* Signatures */}
                <div className="sig-row">
                    <div className="sig-box">Conducted By (Signature)</div>
                    <div className="sig-box">Checked By / Program Lead</div>
                </div>

                {/* Footer Address */}
                <div className="footer">
                    <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
                    <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>
                </div>
            </div>
        </>
    );
}
